import isBrowser from 'is-browser';

// Lazy load DOMPurify only in the browser to avoid bundling jsdom
let DOMPurify: typeof import('dompurify') | null = null;

if (isBrowser) {
  // Only import DOMPurify in the browser (it works natively there)
  DOMPurify = require('dompurify');
}

/**
 * Helper function for regex-based sanitization.
 * Fixes common issues like nested <b> tags and empty tags.
 * 
 * @param html - HTML string to sanitize
 * @returns Sanitized HTML string
 */
const regexSanitize = (html: string): string => {
  return html
    .replace(/<b><b>/g, '<b>')
    .replace(/<\/b><\/b>/g, '</b>')
    .replace(/<b><\/b>/g, '');
};

/**
 * Sanitizes HTML to prevent hangs in markdown-to-jsx.
 * Uses DOMPurify in the browser for robust sanitization, regex-based sanitization.
 * @param html - HTML string to sanitize
 * @returns Sanitized HTML string
 */
export const sanitizeHTML = (html: string): string => {
  if (!html || html.trim() === '') {
    return html;
  }

  if (isBrowser && DOMPurify) {
    try {
      const sanitized = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
          'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'ul', 'ol', 'li', 'dl', 'dt', 'dd',
          'blockquote', 'pre', 'code',
          'a', 'img', 'span', 'div',
          'table', 'thead', 'tbody', 'tr', 'th', 'td',
          'sup', 'sub',
          'Icon', 'DisclaimerMarker', // Custom components
        ],
        ALLOWED_ATTR: [
          'class', 'id', 'style',
          'href', 'target', 'rel',
          'src', 'alt', 'width', 'height', 'data-content',
          'name', 'color', // Icon component attributes
          'disclaimerid', 'displayname', // DisclaimerMarker attributes
          'start', // ol start attribute
          'data-start', 'data-end', // Custom data attributes
        ],
        // Keep relative URLs and data attributes
        ALLOW_DATA_ATTR: true,
        // Preserve custom components
        KEEP_CONTENT: true,
      });

      return regexSanitize(sanitized);
    } catch (error) {
      // Fallback to regex-based sanitization if DOMPurify fails
      console.warn('DOMPurify sanitization failed, using fallback:', error);
    }
  }

  return regexSanitize(html);
};

